import React, { useState, useEffect, useMemo } from 'react';
import { fetchAllProductsReport } from '../services/api';
import ReportTable, { BooleanCell, NumberCell, ProductLink, LinkCell } from './ReportTable';
import { Loader2, AlertCircle, RefreshCw } from 'lucide-react';

const AllProductsReport = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await fetchAllProductsReport();
      setData(result);
    } catch (err) {
      setError('Αποτυχία φόρτωσης δεδομένων');
      console.error('Error loading report:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'productname',
        header: 'Προϊόν',
        cell: ({ row }) => (
          <ProductLink id={row.original.id} name={row.original.productname} />
        ),
      },
      {
        accessorKey: 'qtyinstock',
        header: 'Ποσ.',
        cell: ({ row }) => 
          row.original.skroutzUrl ? (
            <LinkCell href={row.original.skroutzUrl}>{row.original.qtyinstock}</LinkCell>
          ) : (
            row.original.qtyinstock
          ),
      },
      {
        accessorKey: 'freeExpected',
        header: 'Ελ.Αν.Τεμ',
        cell: ({ row }) =>
          row.original.sfUrl ? (
            <LinkCell href={row.original.sfUrl}>{row.original.freeExpected}</LinkCell>
          ) : (
            row.original.freeExpected
          ),
      },
      {
        accessorKey: 'stockPrice',
        header: 'T.A Υπαρ.',
        cell: ({ getValue }) => <NumberCell value={getValue()} />,
      },
      {
        accessorKey: 'vendorPrice',
        header: 'Τιμή Αγ. Κατ.',
        cell: ({ getValue }) => <NumberCell value={getValue()} />,
      },
      {
        accessorKey: 'diffPrice',
        header: 'Διαφορά',
        cell: ({ getValue }) => <NumberCell value={getValue()} />,
      },
      {
        accessorKey: 'premium',
        header: 'Prem.Price',
      },
      {
        accessorKey: 'premSkotsezos',
        header: 'Prem.Skot',
      },
      {
        accessorKey: 'stockprice',
        header: 'Τιμή Χ',
        cell: ({ getValue }) => <NumberCell value={getValue()} />,
      },
      {
        accessorKey: 'exprice',
        header: 'Τιμή EΞ',
        cell: ({ getValue }) => <NumberCell value={getValue()} />,
      },
      {
        accessorKey: 'sales',
        header: 'Πωλ.',
      },
      {
        accessorKey: 'eSales30',
        header: 'Πωλ60',
      },
      {
        accessorKey: 'salesMarketPlace',
        header: 'Πωλ.Skroutz',
      },
      {
        accessorKey: 'salesMarketPlace30',
        header: 'Πωλ.Skroutz30',
      },
      {
        accessorKey: 'xSales30',
        header: 'Πωλ60 Χ',
      },
      {
        accessorKey: 'xSales',
        header: 'Πωλ Χ',
      },
      {
        accessorKey: 'skRevNo',
        header: 'Αξιολ',
      },
      {
        accessorKey: 'skroutzShops',
        header: 'ΑΔΚ',
      },
      {
        accessorKey: 'fixedProfit',
        header: 'ΠΚ',
        cell: ({ getValue }) => <BooleanCell value={getValue()} />,
      },
      {
        accessorKey: 'minimumPrice',
        header: 'Σε κόστος',
        cell: ({ getValue }) => <BooleanCell value={getValue()} />,
      },
      {
        accessorKey: 'ep',
        header: 'Π',
        cell: ({ getValue }) => <BooleanCell value={getValue()} />,
      },
      {
        accessorKey: 'xp',
        header: 'Κ',
        cell: ({ getValue }) => <BooleanCell value={getValue()} />,
      },
      {
        accessorKey: 'toxicx2',
        header: 'ΤΜ',
        cell: ({ getValue }) => <BooleanCell value={getValue()} />,
      },
      {
        accessorKey: 'toxic',
        header: 'ΤΜΝΦΤΤ',
        cell: ({ getValue }) => <BooleanCell value={getValue()} />,
      },
      {
        accessorKey: 'category',
        header: 'Κατηγορία',
      },
      {
        accessorKey: 'toxicx3',
        header: 'Υπερτοξικότητα',
      },
      {
        accessorKey: 'salesSince2020',
        header: 'Πωλήσεις 2020',
      },
      {
        accessorKey: 'problemsSince2020',
        header: 'Προβλήματα 2020',
      },
      {
        accessorKey: 'problemPercentage',
        header: 'Προβλ/Πωλ(%)',
        cell: ({ getValue }) => <NumberCell value={getValue()} />,
      },
      {
        accessorKey: 'bb',
        header: 'BB',
        cell: ({ getValue }) => <BooleanCell value={getValue()} />,
      },
      {
        accessorKey: 'fr',
        header: 'FR',
        cell: ({ getValue }) => <BooleanCell value={getValue()} />,
      },
      {
        accessorKey: 'weight',
        header: 'Βάρος',
      },
    ],
    []
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Φόρτωση αναφοράς...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 p-4">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <span className="ml-2 text-red-700">{error}</span>
          <button
            onClick={loadData}
            className="ml-4 flex items-center rounded bg-red-100 px-3 py-1 text-sm text-red-700 hover:bg-red-200"
          >
            <RefreshCw className="mr-1 h-4 w-4" />
            Επανάληψη
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div className="text-sm text-gray-600">
          Σύνολο: {data.length} προϊόντα
        </div>
        <button
          onClick={loadData}
          className="flex items-center rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          Ανανέωση
        </button>
      </div>
      <ReportTable data={data} columns={columns} title="Όλα τα Προϊόντα" />
    </div>
  );
};

export default AllProductsReport;
